import { clsx } from 'clsx'

export type StatusBadgeSize = 'sm' | 'md' | 'lg'

interface StatusBadgeProps {
  size?: StatusBadgeSize
  /** Colour treatment, e.g. `bg-amber-50 text-amber-700 border border-amber-200`. */
  tone: string
  /** Native tooltip explaining what the badge means. */
  title: string
  /**
   * Render prop for the glyph, called with the size-matched icon classes.
   * A render prop rather than a component so both a lucide icon and a raw
   * inline `<svg>` can be passed without wrapping either.
   */
  icon: (className: string) => React.ReactNode
  children: React.ReactNode
  className?: string
}

const ICON_SIZE: Record<StatusBadgeSize, string> = {
  sm: 'w-2.5 h-2.5',
  md: 'w-3 h-3',
  lg: 'w-3.5 h-3.5',
}

/**
 * Small icon + label recognition pill shown beside member names — the shared
 * scaffold behind `RoleBadge` and `TopExpertBadge`, which previously kept
 * byte-identical copies of these class maps in sync by hand.
 *
 * Distinct from `Badge`, which is the sizeless, icon-less chip used for
 * categories and generic labels.
 */
export function StatusBadge({
  size = 'md',
  tone,
  title,
  icon,
  children,
  className,
}: StatusBadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 font-semibold rounded-full whitespace-nowrap',
        {
          'text-[10px] px-1.5 py-px': size === 'sm',
          'text-[11px] px-2 py-0.5': size === 'md',
          'text-xs px-2.5 py-0.5': size === 'lg',
        },
        tone,
        className
      )}
      title={title}
    >
      {icon(ICON_SIZE[size])}
      {children}
    </span>
  )
}
